import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap"

import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import TableContainerV1 from "components/Common/TableContainerV1"
import ToastDialog from "components/Common/ToastDialog"
import {
  deleteSalesOffice,
  getSalesOfficeList,
  getSalesOfficeFilter,
} from "helpers/UAM/salesoffice.helper"

const TableActionsComponent = (props, handleDeleteSalesOffice = null,roleName) => {
  const navigate = useNavigate()

  const navigateToEditSalesOffice = id => {
    const encryptedId = btoa(id.toString())
    navigate(`/${roleName}/edit-sales-office?id=${encodeURIComponent(encryptedId)}`)
  }
  const navigateToViewSalesOffice = id => {
    const encryptedId = btoa(id.toString())
    navigate(`/${roleName}/view-sales-office?id=${encodeURIComponent(encryptedId)}`)
  }

  return (
    <UncontrolledDropdown>
      <DropdownToggle tag="a" className="card-drop">
        <i className="mdi mdi-dots-horizontal font-size-18"></i>
      </DropdownToggle>

      <DropdownMenu className="dropdown-menu-end">
        <DropdownItem
          onClick={() => {
            navigateToViewSalesOffice(props.id)
          }}
        >
          <i
            className="mdi mdi-eye font-size-16 text-success me-1"
            id="viewtooltrip"
          ></i>
          View
          <UncontrolledTooltip placement="top" target="viewtooltrip">
            View
          </UncontrolledTooltip>
        </DropdownItem>

        <DropdownItem
          onClick={() => {
            navigateToEditSalesOffice(props.id)
          }}
        >
          <i
            className="mdi mdi-pencil font-size-16 text-success me-1"
            id="edittooltip"
          ></i>
          Edit
          <UncontrolledTooltip placement="top" target="edittooltip">
            Edit
          </UncontrolledTooltip>
        </DropdownItem>

        <DropdownItem
          onClick={() => handleDeleteSalesOffice(props.id, props.addressId)}
        >
          <i
            className="mdi mdi-trash-can font-size-16 text-danger me-1"
            id="deletetooltip"
          ></i>
          Delete
          <UncontrolledTooltip placement="top" target="deletetooltip">
            Delete
          </UncontrolledTooltip>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

const SalesOfficeList = props => {
  document.title = "Sales Office List | DMS"

  const navigate = useNavigate()
  const [salesOfficeList, setSalesOfficeList] = useState([])
  const [regionList, setRegionList] = useState([])
  const [filters, setFilters] = useState({})
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  })
  const [pageSize, setPageSize] = useState(10)
  const [searchValue, setSearchValue] = useState("")
  const [columnId, setColumnId] = useState("id")
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc")
  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action", "city", "state"],
  })

  const {roleName} = JSON.parse(localStorage.getItem("user"))

  const [toastInfo, setToastInfo] = useState({ showToast: false })
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteItemId, setDeleteItemId] = useState(null)

  const columns = useMemo(
    () => [
      {
        Header: "Sales Office Code",
        accessor: "salesOfficeCode",
        filterable: true,
        Cell: cell => {
          return cell.value ? cell.value : "-"
        },
      },
      {
        Header: "Sales Office Name",
        accessor: "salesOfficeName",
        filterable: true,
        Cell: cell => {
          return cell.value ? cell.value : "-"
        },
      },
      {
        Header: "Region",
        accessor: "region",
        filterable: true,
        Cell: cell => {
          return cell.value ? cell.value : "-"
        },
      },

      {
        Header: "City",
        accessor: "city",
        filterable: true,
        Cell: cell => {
          return cell.value ? cell.value : "-"
        },
      },
      {
        Header: "State",
        accessor: "state",
        filterable: true,
        Cell: cell => {
          return cell.value ? cell.value : "-"
        },
      },
      {
        Header: "Action",
        Cell: cellProps => {
          const rowData = cellProps.row.original
          return TableActionsComponent(rowData, handleDeleteSalesOffice,roleName)
        },
      },
    ],
    []
  )

  const handleDeleteSalesOffice = (id, addressId) => {
    setDeleteItemId({ id, addressId })
    setDeleteModal(true)
  }

  const deleteSalesOfficeHelper = () => {
    deleteSalesOffice(deleteItemId)
        .then(responseFromAPI => {
            if (responseFromAPI.status && responseFromAPI.code == 200) {
                setToastInfo({
                    message: responseFromAPI.message,
                    showToast: true,
                    role: "success",
                  })
                setDeleteModal(false)
                getSalesOfficeListHelper({})
            }else{
                setDeleteModal(false)
                setToastInfo({ message: responseFromAPI.message, showToast: true, role: "error" });
            }
        })
        .catch(error => { 
            setDeleteModal(false)
        })
  }

  const getSalesOfficeListHelper = data => {
    getSalesOfficeList(data)
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
            let newData = responseFromAPI.data.data.map(item => {
              let newItem = { ...item.address, ...item }
              delete newItem.address
              return newItem
            })
            setSalesOfficeList(newData)
            const { totalItems, totalPages, currentPage } = responseFromAPI.data
            setPagination({
              pageIndex: currentPage,
              pageCount: totalPages,
              totalRecords: totalItems,
            })
        } else {
          setPagination({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
          setSalesOfficeList([])
        }
      })
      .catch(error => {})
  }

  const getSalesOfficeFilterOptions = () => {
    getSalesOfficeFilter()
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
          const { regions } = responseFromAPI.data
          let regionList = regions[0]?.dropdownValues.map(item => {
            return item.dropdownValue
          })
          setRegionList(regionList)
        }
      })
      .catch(error => {})
  }

  const handlePageChange = (
    page,
    pageSize,
    searchKey= searchValue,
    columnName = columnId,
    sortingOrder = sortingOrderStatus,
    filterType = filters
  ) => {
    setPageSize(pageSize)
    getSalesOfficeListHelper({
      page,
      limit: pageSize,
      searchKey,
      columnName,
      sortingOrder,
      filterType,
    })
  }

  const handleSortColumn = option => {
    setSortingOptions(option)
    const columnName = option.column
    setColumnId(columnName)
    const sortingOrder = option.isAsc ? "asc" : "desc"
    setSortingOrderStatus(sortingOrder)
    handlePageChange(1, pageSize, searchValue, columnName, sortingOrder)
  }

  const handleSearchFilter = (value, pageSize) => {
    setSearchValue(value)
    handlePageChange(1, pageSize, value)
  }
  const handleClearSearch = () => {
    setSearchValue("")
    setFilters({})
    handlePageChange(1, pageSize, "", columnId, sortingOrderStatus, {})
  }

  useEffect(() => {
    getSalesOfficeListHelper({})
    getSalesOfficeFilterOptions()
  }, [])

  const handleCustomerClicks = () => {
    navigate(`/${roleName}/add-sales-office`)
  }

  const handleRegionFilter = value => {
    setFilters({ ...filters, region: value })

    handlePageChange(1, pageSize, searchValue, columnId, sortingOrderStatus, {
      ...filters,
      region: value,
    })
  }

  const customeFilterSection = (
    <div className="text-sm-start">
      <select
        className="form-select  me-3 show-dropdown"
        style={{ width: "150px" }}
        onChange={e => {
          handleRegionFilter(e.target.value)
        }}
      >
        <option selected={filters.region == ""} value="">
          All Region
        </option>
        {regionList.map((item, index) => {
          return (
            <option selected={filters.region == item} key={item} value={item}>
              {item}
            </option>
          )
        })}
      </select>
    </div>
  )

  const buttonSection = (
    <div className="text-sm-end">
      <Button
        type="button"
        color="success"
        className="btn-rounded mb-2 me-2"
        onClick={handleCustomerClicks}
      >
        <i className="mdi mdi-plus me-1" />
        {"Add Sales Office"}
      </Button>
    </div>
  )

  return (
    <div className="page-content">
      <Breadcrumbs title="Sales Office" breadcrumbItem="Sales Office List" />
      <Container fluid>
        <Row className="vertical-scroll">
          <Col xs="12">
            <Card>
              <CardBody>
                <TableContainerV1
                  columns={columns}
                  data={salesOfficeList}
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  handleSearchFilter={handleSearchFilter}
                  searchHelperText="Search by Sales Office Name, Code, Region"
                  customFilterSection={customeFilterSection}
                  customSection={buttonSection}
                  sortOptions={sortingOptions}
                  handleSortColumn={handleSortColumn}
                  handleClearSearch={handleClearSearch}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteSalesOfficeHelper}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ToastDialog
        message={toastInfo.message || ""}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      ></ToastDialog>
    </div>
  )
}

export default SalesOfficeList
